import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Sparkles, Loader2, Bell, TrendingUp, Phone, MessageSquare, Copy, Bot, PhoneCall,
  Plus, X, AlertTriangle, CheckCircle2, PlayCircle, Ban, Shield, Search, ChevronDown,
} from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Floating } from '@/components/ui/Floating';
import { formatDateTime } from '@/lib/dateFormat';
import { SelectField } from '@/components/ui/SelectField';

type Tab = 'assistant' | 'agents' | 'calls' | 'settings';

interface ProspectLite { id: string; contactName: string; companyName: string; status: string }
interface VoiceAgent {
  id: string; name: string; description?: string | null; provider: string;
  providerAgentId?: string | null; providerSyncError?: string | null; voiceId?: string | null; language: string;
  firstMessage?: string | null; systemPrompt?: string | null; maxDurationSec: number; isActive: boolean;
}
interface VoiceCall {
  id: string; status: string; toNumber: string; direction: string;
  durationSec?: number | null; summary?: string | null; outcome?: string | null;
  recordingUrl?: string | null; transcript?: unknown; error?: string | null; createdAt: string;
  agent?: { id: string; name: string } | null;
  prospect?: { id: string; contactName: string; companyName: string } | null;
}
interface OptOut { id: string; phoneNorm: string; reason?: string | null; createdAt: string }

const inputCls = 'w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent';
const INP = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40 focus:border-[#6366F1]';
const STATUS_STYLE: Record<string, string> = {
  queued: 'bg-slate-100 text-slate-600', ringing: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700', completed: 'bg-emerald-100 text-emerald-700',
  no_answer: 'bg-slate-100 text-slate-500', busy: 'bg-slate-100 text-slate-500',
  failed: 'bg-red-100 text-red-700', canceled: 'bg-slate-100 text-slate-500', simulated: 'bg-violet-100 text-violet-700',
};
const fmtDur = (s?: number | null) => {
  if (!s || s < 0) return '—';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};
const emptyAgent: Partial<VoiceAgent> = {
  name: '', description: '', voiceId: '', language: 'en', firstMessage: '', systemPrompt: '',
  providerAgentId: '', maxDurationSec: 300, isActive: true,
};

export default function SuperAdminAiPage() {
  const [tab, setTab] = useState<Tab>('assistant');

  return (
    <SuperAdminLayout title="AI Assistant">
      <div className="mx-auto max-w-5xl space-y-5">
        <p className="text-sm text-[#64748B]">Drafts, alerts, insights and AI voice calls for your prospect pipeline — provider &amp; script under your control.</p>

        <div className="flex gap-1 border-b border-gray-100">
          {([['assistant', 'Assistant'], ['agents', 'Voice Agents'], ['calls', 'Call Log'], ['settings', 'AI Settings']] as [Tab, string][]).map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === k ? 'border-[#6366F1] text-[#6366F1]' : 'border-transparent text-[#64748B] hover:text-[#1E293B]'}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'assistant' && <AssistantTab />}
        {tab === 'agents' && <VoiceAgentsTab />}
        {tab === 'calls' && <CallLogTab />}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </SuperAdminLayout>
  );
}

// ── Assistant tab ───────────────────────────────────────────────────────────
function AssistantTab() {
  const [status, setStatus] = useState<{ enabled: boolean; configured: boolean; provider: string } | null>(null);
  const [alerts, setAlerts] = useState<Array<{ type: string; title: string; count: number }>>([]);
  const [insights, setInsights] = useState<{ total: number; byStatus: Record<string, number>; winRate: number; openValue: number } | null>(null);

  const [prospects, setProspects] = useState<ProspectLite[]>([]);
  const [prospectId, setProspectId] = useState('');
  const [draftType, setDraftType] = useState<'whatsapp' | 'email'>('whatsapp');
  const [draft, setDraft] = useState('');
  const [drafting, setDrafting] = useState(false);

  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    saApi.get('/super-admin/ai/status').then(r => setStatus(r.data)).catch(() => {});
    saApi.get('/super-admin/ai/smart-alerts').then(r => setAlerts(r.data?.alerts ?? [])).catch(() => {});
    saApi.get('/super-admin/ai/insights').then(r => setInsights(r.data)).catch(() => {});
    saApi.get('/super-admin/prospects', { params: { limit: 100 } }).then(r => setProspects(r.data?.prospects ?? [])).catch(() => {});
  }, []);

  const runDraft = async () => {
    if (!prospectId) { toast('error', 'Pick a prospect first'); return; }
    setDrafting(true); setDraft('');
    try {
      const { data } = await saApi.post('/super-admin/ai/draft', { prospectId, type: draftType });
      setDraft(data?.text || '');
    } catch { toast('error', 'Draft failed'); }
    finally { setDrafting(false); }
  };

  const ask = async () => {
    if (!question.trim()) return;
    setAsking(true); setAnswer('');
    try {
      const { data } = await saApi.post('/super-admin/ai/query', { query: question.trim() });
      setAnswer(data?.text || '');
    } catch { toast('error', 'Ask failed'); }
    finally { setAsking(false); }
  };

  return (
    <div className="space-y-5">
      <div className={`rounded-xl border px-4 py-3 text-sm flex items-center gap-2 ${status?.configured ? 'border-emerald-100 bg-emerald-50 text-emerald-800' : 'border-amber-100 bg-amber-50 text-amber-800'}`}>
        <Sparkles className="h-4 w-4" />
        {status == null ? 'Checking AI…'
          : status.configured ? <>AI is <b>on</b> ({status.provider}). Drafts &amp; answers use your model.</>
          : <>AI key not set — drafts use built-in templates. Add a key in <b>AI Settings</b>.</>}
      </div>

      {insights && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ['Prospects', String(insights.total)],
            ['Win rate', `${insights.winRate}%`],
            ['Open value', `₹${insights.openValue.toLocaleString('en-IN')}`],
            ['New', String(insights.byStatus['new'] ?? 0)],
          ].map(([label, val]) => (
            <div key={label} className="rounded-xl border border-gray-100 bg-white px-4 py-3">
              <div className="text-[11px] uppercase tracking-wide text-[#94A3B8]">{label}</div>
              <div className="text-lg font-bold text-[#1E293B] mt-0.5">{val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-gray-100 bg-white p-4">
        <p className="text-sm font-semibold text-[#1E293B] flex items-center gap-2 mb-2"><Bell className="h-4 w-4 text-amber-500" /> Needs attention today</p>
        {alerts.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">All caught up — no urgent follow-ups.</p>
        ) : (
          <div className="space-y-1.5">
            {alerts.map(a => (
              <div key={a.type} className="flex items-center justify-between text-sm">
                <span className="text-[#334155]">{a.title}</span>
                <span className="font-semibold text-[#6366F1]">{a.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-4 space-y-3">
        <p className="text-sm font-semibold text-[#1E293B] flex items-center gap-2"><MessageSquare className="h-4 w-4 text-[#6366F1]" /> Draft with AI</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1"><ProspectPicker prospects={prospects} value={prospectId} onChange={setProspectId} /></div>
          <SelectField className={`${inputCls} sm:w-40`} value={draftType} onChange={e => setDraftType(e.target.value as any)}>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
          </SelectField>
          <button onClick={runDraft} disabled={drafting}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#6366F1] px-4 h-10 text-sm font-medium text-white hover:bg-[#4F46E5] disabled:opacity-50 flex-shrink-0">
            {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Draft
          </button>
        </div>
        {draft && (
          <div className="relative rounded-lg border border-gray-100 bg-slate-50 p-3">
            <pre className="whitespace-pre-wrap text-sm text-[#334155] font-sans">{draft}</pre>
            <button onClick={() => navigator.clipboard.writeText(draft).then(() => toast('success', 'Copied'))}
              className="absolute top-2 right-2 text-slate-400 hover:text-slate-700" aria-label="Copy"><Copy className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-4 space-y-3">
        <p className="text-sm font-semibold text-[#1E293B] flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-500" /> Ask about your pipeline</p>
        <div className="flex gap-2">
          <input className={inputCls} placeholder="e.g. Which prospects should I follow up first?" value={question}
            onChange={e => setQuestion(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') ask(); }} />
          <button onClick={ask} disabled={asking}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#6366F1] px-4 h-10 text-sm font-medium text-white hover:bg-[#4F46E5] disabled:opacity-50 flex-shrink-0">
            {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ask'}
          </button>
        </div>
        {answer && <div className="rounded-lg border border-gray-100 bg-slate-50 p-3"><pre className="whitespace-pre-wrap text-sm text-[#334155] font-sans">{answer}</pre></div>}
      </div>
    </div>
  );
}

// ── Voice Agents tab ─────────────────────────────────────────────────────────
function VoiceAgentsTab() {
  const [status, setStatus] = useState<{ enabled: boolean; provider: string; configured: boolean } | null>(null);
  const [agents, setAgents] = useState<VoiceAgent[]>([]);
  const [voices, setVoices] = useState<{ voice_id: string; voice_name: string; provider: string }[]>([]);
  const [prospects, setProspects] = useState<ProspectLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<VoiceAgent> | null>(null);
  const [saving, setSaving] = useState(false);
  const [placing, setPlacing] = useState<{ agentId: string; prospectId: string; toNumber: string } | null>(null);
  const [confirmDel, setConfirmDel] = useState<VoiceAgent | null>(null);
  const [delLoading, setDelLoading] = useState(false);
  const [apiDown, setApiDown] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // allSettled so one failing call never blanks the whole tab; a rejected
    // agents call means the voice API isn't reachable yet (backend deploy).
    const [s, a, v, p] = await Promise.allSettled([
      saApi.get('/super-admin/voice/status'),
      saApi.get('/super-admin/voice/agents'),
      saApi.get('/super-admin/voice/voices'),
      saApi.get('/super-admin/prospects', { params: { limit: 100 } }),
    ]);
    setApiDown(a.status === 'rejected');
    if (s.status === 'fulfilled') setStatus(s.value.data);
    if (a.status === 'fulfilled') setAgents(a.value.data ?? []);
    if (v.status === 'fulfilled') setVoices(v.value.data ?? []);
    if (p.status === 'fulfilled') setProspects(p.value.data?.prospects ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveAgent = async () => {
    if (!editing?.name?.trim()) { toast('error', 'Name is required'); return; }
    setSaving(true);
    try {
      const body = {
        name: editing.name, description: editing.description, voiceId: editing.voiceId, language: editing.language || 'en',
        firstMessage: editing.firstMessage, systemPrompt: editing.systemPrompt, providerAgentId: editing.providerAgentId,
        maxDurationSec: editing.maxDurationSec ?? 300, isActive: editing.isActive ?? true,
      };
      if (editing.id) await saApi.patch(`/super-admin/voice/agents/${editing.id}`, body);
      else await saApi.post('/super-admin/voice/agents', body);
      toast('success', 'Agent saved'); setEditing(null); load();
    } catch (e: any) { toast('error', e?.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const syncAgent = async (id: string) => {
    try {
      const { data } = await saApi.post(`/super-admin/voice/agents/${id}/sync`);
      toast(data?.synced ? 'success' : 'error', data?.synced ? 'Synced to provider' : (data?.error || 'Sync failed'));
      load();
    } catch { toast('error', 'Sync failed'); }
  };

  const confirmRemove = async () => {
    if (!confirmDel) return;
    setDelLoading(true);
    try { await saApi.delete(`/super-admin/voice/agents/${confirmDel.id}`); toast('success', 'Agent deleted'); setConfirmDel(null); load(); }
    catch { toast('error', 'Delete failed'); }
    finally { setDelLoading(false); }
  };

  const placeCall = async () => {
    if (!placing) return;
    if (!placing.prospectId && !placing.toNumber.trim()) { toast('error', 'Pick a prospect or enter a number'); return; }
    setSaving(true);
    try {
      await saApi.post('/super-admin/voice/calls', {
        agentId: placing.agentId,
        prospectId: placing.prospectId || undefined,
        toNumber: placing.toNumber.trim() || undefined,
      });
      toast('success', 'Call placed'); setPlacing(null);
    } catch (e: any) { toast('error', e?.response?.data?.message || 'Call could not be placed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {apiDown && (
        <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>Voice service isn’t reachable yet. If you just deployed, wait for the backend (Railway) to finish — the Voice endpoints and tables come online on the next backend deploy. <button onClick={load} className="underline font-medium">Retry</button></span>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <div className={`text-sm ${status?.configured ? 'text-emerald-700' : 'text-amber-700'}`}>
          {apiDown ? '' : status == null ? 'Checking…'
            : status.configured ? <>Voice provider ready ({status.provider}){!status.enabled && ' — but calling is OFF (enable in AI Settings)'}</>
            : 'Voice provider not configured — add your key & number in AI Settings.'}
        </div>
        <button onClick={() => setEditing({ ...emptyAgent })}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] px-4 h-10 text-sm font-medium text-white hover:bg-[#4F46E5] flex-shrink-0">
          <Plus className="h-4 w-4" /> New Agent
        </button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
      ) : agents.length === 0 ? (
        <EmptyBox icon={<Bot className="h-8 w-8" />} title="No voice agents yet" hint="Create an agent (voice + script), sync it to your provider, then call prospects." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {agents.map(a => (
            <div key={a.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center"><Bot className="h-5 w-5 text-[#6366F1]" /></div>
                  <div>
                    <div className="font-semibold text-slate-800">{a.name}</div>
                    <div className="text-xs text-slate-400 capitalize">{a.provider} · {a.language}</div>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${a.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{a.isActive ? 'Active' : 'Off'}</span>
              </div>
              {a.description && <p className="text-sm text-slate-500 mt-3 line-clamp-2">{a.description}</p>}
              <div className="mt-3">
                {a.providerSyncError ? (
                  <span className="inline-flex items-center gap-1 text-xs text-red-600" title={a.providerSyncError}><AlertTriangle className="h-3.5 w-3.5" /> Sync failed</span>
                ) : a.providerAgentId ? (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Synced to provider</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-400"><AlertTriangle className="h-3.5 w-3.5" /> Not synced</span>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-2 flex-wrap">
                <button onClick={() => setPlacing({ agentId: a.id, prospectId: '', toNumber: '' })} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#6366F1] hover:underline"><PhoneCall className="h-4 w-4" /> Call</button>
                <span className="text-slate-200">·</span>
                <button onClick={() => syncAgent(a.id)} className="text-sm text-slate-600 hover:text-slate-900">Sync</button>
                <span className="text-slate-200">·</span>
                <button onClick={() => setEditing(a)} className="text-sm text-slate-600 hover:text-slate-900">Edit</button>
                <span className="text-slate-200">·</span>
                <button onClick={() => setConfirmDel(a)} className="text-sm text-red-500 hover:text-red-700">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Agent editor drawer */}
      {editing && (
        <Drawer title={editing.id ? 'Edit Agent' : 'New Agent'} onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <Field label="Name *"><input value={editing.name ?? ''} onChange={e => setEditing({ ...editing, name: e.target.value })} className={INP} placeholder="Welcome Caller" /></Field>
            <Field label="Description"><input value={editing.description ?? ''} onChange={e => setEditing({ ...editing, description: e.target.value })} className={INP} placeholder="Calls new prospects within 60s" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Voice">
                {voices.length > 0 ? (
                  <SelectField value={editing.voiceId ?? ''} onChange={e => setEditing({ ...editing, voiceId: e.target.value })} className={INP}>
                    <option value="">Default (11labs-Adrian)</option>
                    {voices.map(v => <option key={v.voice_id} value={v.voice_id}>{v.voice_name} ({v.provider})</option>)}
                  </SelectField>
                ) : (
                  <input value={editing.voiceId ?? ''} onChange={e => setEditing({ ...editing, voiceId: e.target.value })} className={INP} placeholder="11labs-Adrian" />
                )}
              </Field>
              <Field label="Language"><input value={editing.language ?? 'en'} onChange={e => setEditing({ ...editing, language: e.target.value })} className={INP} placeholder="en" /></Field>
            </div>
            <p className="-mt-2 text-[11px] text-slate-400">{voices.length > 0 ? 'Voices loaded from your Retell account. Leave as Default if unsure.' : 'Add & save your Retell key first to load the voice list. Meanwhile use a Retell id like 11labs-Adrian, or leave blank.'}</p>
            <Field label="Provider Agent ID"><input value={editing.providerAgentId ?? ''} onChange={e => setEditing({ ...editing, providerAgentId: e.target.value })} className={INP} placeholder="agent_… (auto-filled after Sync)" /></Field>
            <Field label="First message (opening line — AI disclosure)"><textarea value={editing.firstMessage ?? ''} onChange={e => setEditing({ ...editing, firstMessage: e.target.value })} className={`${INP} min-h-[64px]`} placeholder="Hi, this is an AI assistant from BrixLead…" /></Field>
            <Field label="Script / system prompt (what to say)"><textarea value={editing.systemPrompt ?? ''} onChange={e => setEditing({ ...editing, systemPrompt: e.target.value })} className={`${INP} min-h-[140px]`} placeholder="You are a friendly BrixLead sales rep. Goal: qualify interest, team size and timeline, then offer to book a demo…" /></Field>
            <div className="grid grid-cols-2 gap-3 items-end">
              <Field label="Max duration (sec)"><input type="number" value={editing.maxDurationSec ?? 300} onChange={e => setEditing({ ...editing, maxDurationSec: Number(e.target.value) })} className={INP} /></Field>
              <label className="flex items-center gap-2 text-sm text-slate-700 pb-2"><input type="checkbox" checked={editing.isActive ?? true} onChange={e => setEditing({ ...editing, isActive: e.target.checked })} className="h-4 w-4 rounded" /> Active</label>
            </div>
          </div>
          <div className="flex gap-2 pt-5">
            <button onClick={saveAgent} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#6366F1] text-white text-sm font-semibold hover:bg-[#4F46E5] disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
            <button onClick={() => setEditing(null)} className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-slate-600 hover:bg-gray-50">Cancel</button>
          </div>
        </Drawer>
      )}

      {/* Place-call drawer */}
      {placing && (
        <Drawer title="Place AI call" onClose={() => setPlacing(null)}>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">The AI agent will call this prospect. Pick a prospect (its phone is used) or type a number in E.164 format.</p>
            <Field label="Prospect">
              <ProspectPicker prospects={prospects} value={placing.prospectId}
                onChange={id => setPlacing({ ...placing, prospectId: id })}
                includeNone noneLabel="— None (use number below) —" />
            </Field>
            <Field label="Phone number (optional override)"><input value={placing.toNumber} onChange={e => setPlacing({ ...placing, toNumber: e.target.value })} className={INP} placeholder="+919876543210" /></Field>
          </div>
          <div className="flex gap-2 pt-5">
            <button onClick={placeCall} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#6366F1] text-white text-sm font-semibold hover:bg-[#4F46E5] disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />} Call now</button>
            <button onClick={() => setPlacing(null)} className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-slate-600 hover:bg-gray-50">Cancel</button>
          </div>
        </Drawer>
      )}

      <ConfirmModal
        isOpen={!!confirmDel}
        title="Delete Voice Agent?"
        message={`Are you sure you want to delete "${confirmDel?.name ?? 'this agent'}"? This cannot be undone.`}
        confirmText="Delete"
        onConfirm={confirmRemove}
        onCancel={() => setConfirmDel(null)}
        isLoading={delLoading}
      />
    </div>
  );
}

// ── Call Log tab ─────────────────────────────────────────────────────────────
function CallLogTab() {
  const [calls, setCalls] = useState<VoiceCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiDown, setApiDown] = useState(false);
  const [detail, setDetail] = useState<VoiceCall | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await saApi.get('/super-admin/voice/calls', { params: { limit: 50 } }); setCalls(data ?? []); setApiDown(false); }
    catch { setApiDown(true); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
  if (apiDown) return (
    <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
      <span>Call log isn’t reachable yet. If you just deployed, wait for the backend (Railway) to finish deploying, then <button onClick={load} className="underline font-medium">retry</button>.</span>
    </div>
  );
  if (calls.length === 0) return <EmptyBox icon={<PhoneCall className="h-8 w-8" />} title="No calls yet" hint="Place a call from any agent — the history will appear here." />;

  return (
    <div>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#6366F1] text-white text-left">
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">To</th>
                <th className="px-4 py-3 font-semibold">Prospect</th>
                <th className="px-4 py-3 font-semibold">Agent</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Duration</th>
              </tr>
            </thead>
            <tbody>
              {calls.map(c => (
                <tr key={c.id} onClick={() => setDetail(c)} className="border-b border-gray-50 hover:bg-indigo-50/40 cursor-pointer">
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDateTime(c.createdAt)}</td>
                  <td className="px-4 py-3 text-slate-700 font-medium tabular-nums">{c.toNumber}</td>
                  <td className="px-4 py-3 text-slate-600">{c.prospect?.contactName ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{c.agent?.name ?? '—'}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLE[c.status] ?? 'bg-slate-100 text-slate-600'}`}>{c.status.replace('_', ' ')}</span></td>
                  <td className="px-4 py-3 text-right text-slate-600 tabular-nums">{fmtDur(c.durationSec)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <Drawer title="Call detail" onClose={() => setDetail(null)}>
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Info label="To" value={detail.toNumber} />
              <Info label="Status" value={detail.status.replace('_', ' ')} />
              <Info label="Prospect" value={detail.prospect?.contactName ?? '—'} />
              <Info label="Agent" value={detail.agent?.name ?? '—'} />
              <Info label="Duration" value={fmtDur(detail.durationSec)} />
              <Info label="Outcome" value={detail.outcome ?? '—'} />
            </div>
            {detail.recordingUrl && (<div><div className="text-xs font-medium text-slate-400 mb-1">Recording</div><audio controls src={detail.recordingUrl} className="w-full" /></div>)}
            {detail.summary && (<div><div className="text-xs font-medium text-slate-400 mb-1">Summary</div><p className="text-slate-700 bg-slate-50 rounded-lg p-3">{detail.summary}</p></div>)}
            {detail.error && (<div><div className="text-xs font-medium text-red-400 mb-1">Error</div><p className="text-red-600 bg-red-50 rounded-lg p-3">{detail.error}</p></div>)}
            {!!detail.transcript && (
              <div>
                <div className="text-xs font-medium text-slate-400 mb-1 flex items-center gap-1"><PlayCircle className="h-3.5 w-3.5" /> Transcript</div>
                <pre className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap max-h-72 overflow-auto">{typeof detail.transcript === 'string' ? detail.transcript : JSON.stringify(detail.transcript, null, 2)}</pre>
              </div>
            )}
          </div>
        </Drawer>
      )}
    </div>
  );
}

// ── AI Settings tab (AI provider + Voice provider + DNC) ─────────────────────
function SettingsTab() {
  // AI (text)
  const [enabled, setEnabled] = useState(true);
  const [provider, setProvider] = useState('anthropic');
  const [apiKey, setApiKey] = useState('');
  const [keyMasked, setKeyMasked] = useState(false);
  const [models, setModels] = useState({ anthropicModel: '', openaiModel: '', geminiModel: '' });
  const [savingAi, setSavingAi] = useState(false);
  // Voice
  const [v, setV] = useState({
    enabled: false, provider: 'retell', apiKey: '', fromNumber: '', elevenLabsPhoneNumberId: '', defaultAgentId: '',
    requireConsent: false, callWindowStart: '09:00', callWindowEnd: '21:00', callTimezone: 'Asia/Kolkata', dailyCap: '0',
  });
  const [vKeyMasked, setVKeyMasked] = useState(false);
  const [savingVoice, setSavingVoice] = useState(false);
  // DNC
  const [optOuts, setOptOuts] = useState<OptOut[]>([]);
  const [newDnc, setNewDnc] = useState({ phone: '', reason: '' });

  const load = useCallback(async () => {
    try {
      const { data } = await saApi.get('/super-admin/settings/integrations');
      const ai = data?.ai ?? {};
      setEnabled(ai.enabled !== false);
      setProvider((ai.provider || 'anthropic').toLowerCase());
      setKeyMasked(typeof ai.apiKey === 'string' && ai.apiKey.startsWith('****'));
      setApiKey('');
      setModels({
        anthropicModel: ai.anthropicModel || 'claude-sonnet-4-20250514',
        openaiModel: ai.openaiModel || 'gpt-4o',
        geminiModel: ai.geminiModel || 'gemini-2.0-flash',
      });
      const vo = data?.voice ?? {};
      setVKeyMasked(typeof vo.apiKey === 'string' && vo.apiKey.startsWith('****'));
      setV({
        enabled: vo.enabled === true,
        provider: (vo.provider || 'retell').toLowerCase(),
        apiKey: '',
        fromNumber: vo.fromNumber || '',
        elevenLabsPhoneNumberId: vo.elevenLabsPhoneNumberId || '',
        defaultAgentId: vo.defaultAgentId || '',
        requireConsent: vo.requireConsent === true,
        callWindowStart: vo.callWindowStart || '09:00',
        callWindowEnd: vo.callWindowEnd || '21:00',
        callTimezone: vo.callTimezone || 'Asia/Kolkata',
        dailyCap: String(vo.dailyCap ?? '0'),
      });
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadDnc = useCallback(async () => {
    try { const { data } = await saApi.get('/super-admin/voice/optouts'); setOptOuts(data ?? []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadDnc(); }, [loadDnc]);

  const saveAi = async () => {
    setSavingAi(true);
    try {
      const ai: any = { enabled, provider, ...models };
      if (apiKey.trim()) ai.apiKey = apiKey.trim();
      await saApi.patch('/super-admin/settings/integrations', { ai });
      toast('success', 'AI settings saved'); load();
    } catch (e: any) { toast('error', e?.response?.data?.message || 'Save failed'); }
    finally { setSavingAi(false); }
  };

  const saveVoice = async () => {
    setSavingVoice(true);
    try {
      const voice: any = {
        enabled: v.enabled, provider: v.provider, fromNumber: v.fromNumber, elevenLabsPhoneNumberId: v.elevenLabsPhoneNumberId,
        defaultAgentId: v.defaultAgentId, requireConsent: v.requireConsent,
        callWindowStart: v.callWindowStart, callWindowEnd: v.callWindowEnd, callTimezone: v.callTimezone,
        dailyCap: Number(v.dailyCap) || 0,
      };
      if (v.apiKey.trim()) voice.apiKey = v.apiKey.trim();
      await saApi.patch('/super-admin/settings/integrations', { voice });
      toast('success', 'Voice settings saved'); load();
    } catch (e: any) { toast('error', e?.response?.data?.message || 'Save failed'); }
    finally { setSavingVoice(false); }
  };

  const addDnc = async () => {
    if (!newDnc.phone.trim()) return;
    try { await saApi.post('/super-admin/voice/optouts', { phone: newDnc.phone.trim(), reason: newDnc.reason.trim() || undefined }); setNewDnc({ phone: '', reason: '' }); loadDnc(); }
    catch (e: any) { toast('error', e?.response?.data?.message || 'Failed to add'); }
  };
  const removeDnc = async (id: string) => {
    try { await saApi.delete(`/super-admin/voice/optouts/${id}`); loadDnc(); } catch { toast('error', 'Failed to remove'); }
  };

  return (
    <div className="space-y-6 max-w-xl">
      {/* AI provider */}
      <div className="rounded-xl border border-gray-100 bg-white p-5 space-y-4">
        <p className="text-sm font-semibold text-[#1E293B] flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#6366F1]" /> AI (text) — drafts &amp; answers</p>
        <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50">
          <div><p className="text-sm font-medium text-[#1E293B]">AI enabled</p><p className="text-xs text-[#94A3B8]">Off = drafts/answers use built-in templates (no AI cost).</p></div>
          <Toggle checked={enabled} onChange={setEnabled} />
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-normal text-[#64748B]">Provider</label>
          <SelectField className={inputCls} value={provider} onChange={e => setProvider(e.target.value)}>
            <option value="anthropic">Anthropic (Claude)</option>
            <option value="openai">OpenAI</option>
            <option value="gemini">Google Gemini</option>
          </SelectField>
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-normal text-[#64748B]">API key</label>
          <input type="password" className={inputCls} autoComplete="new-password"
            placeholder={keyMasked ? '•••••••••• (saved — paste to replace)' : 'Paste API key'}
            value={apiKey} onChange={e => setApiKey(e.target.value)} />
          <p className="text-[11px] text-slate-400">Anthropic can fall back to the platform env key; OpenAI/Gemini need a key here.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5"><label className="block text-xs font-normal text-[#64748B]">Claude model</label><input className={inputCls} value={models.anthropicModel} onChange={e => setModels(p => ({ ...p, anthropicModel: e.target.value }))} /></div>
          <div className="space-y-1.5"><label className="block text-xs font-normal text-[#64748B]">OpenAI model</label><input className={inputCls} value={models.openaiModel} onChange={e => setModels(p => ({ ...p, openaiModel: e.target.value }))} /></div>
          <div className="space-y-1.5"><label className="block text-xs font-normal text-[#64748B]">Gemini model</label><input className={inputCls} value={models.geminiModel} onChange={e => setModels(p => ({ ...p, geminiModel: e.target.value }))} /></div>
        </div>
        <div className="flex justify-end">
          <button onClick={saveAi} disabled={savingAi} className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] px-4 h-10 text-sm font-medium text-white hover:bg-[#4F46E5] disabled:opacity-50">{savingAi && <Loader2 className="h-4 w-4 animate-spin" />} Save AI</button>
        </div>
      </div>

      {/* Voice provider */}
      <div className="rounded-xl border border-gray-100 bg-white p-5 space-y-4">
        <p className="text-sm font-semibold text-[#1E293B] flex items-center gap-2"><Phone className="h-4 w-4 text-[#6366F1]" /> AI Voice — outbound calls</p>
        <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50">
          <div><p className="text-sm font-medium text-[#1E293B]">Voice calling enabled</p><p className="text-xs text-[#94A3B8]">Master switch. Each call burns provider + telephony credits.</p></div>
          <Toggle checked={v.enabled} onChange={val => setV(p => ({ ...p, enabled: val }))} />
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-normal text-[#64748B]">Provider</label>
          <SelectField className={inputCls} value={v.provider} onChange={e => setV(p => ({ ...p, provider: e.target.value }))}>
            <option value="retell">Retell AI</option>
            <option value="elevenlabs">ElevenLabs</option>
          </SelectField>
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-normal text-[#64748B]">Provider API key</label>
          <input type="password" className={inputCls} autoComplete="new-password"
            placeholder={vKeyMasked ? '•••••••••• (saved — paste to replace)' : 'Paste provider API key'}
            value={v.apiKey} onChange={e => setV(p => ({ ...p, apiKey: e.target.value }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><label className="block text-xs font-normal text-[#64748B]">From number (Retell caller ID)</label><input className={inputCls} value={v.fromNumber} onChange={e => setV(p => ({ ...p, fromNumber: e.target.value }))} placeholder="+1…" /></div>
          <div className="space-y-1.5"><label className="block text-xs font-normal text-[#64748B]">ElevenLabs Phone Number ID</label><input className={inputCls} value={v.elevenLabsPhoneNumberId} onChange={e => setV(p => ({ ...p, elevenLabsPhoneNumberId: e.target.value }))} placeholder="phnum_…" /></div>
        </div>
        <div className="space-y-1.5"><label className="block text-xs font-normal text-[#64748B]">Default provider agent ID (fallback)</label><input className={inputCls} value={v.defaultAgentId} onChange={e => setV(p => ({ ...p, defaultAgentId: e.target.value }))} placeholder="agent_…" /></div>

        <div className="pt-2 border-t border-gray-50">
          <p className="text-xs font-semibold text-[#64748B] flex items-center gap-1.5 mb-2"><Shield className="h-3.5 w-3.5" /> Compliance guardrails</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5"><label className="block text-xs font-normal text-[#64748B]">Hours start</label><input className={inputCls} value={v.callWindowStart} onChange={e => setV(p => ({ ...p, callWindowStart: e.target.value }))} placeholder="09:00" /></div>
            <div className="space-y-1.5"><label className="block text-xs font-normal text-[#64748B]">Hours end</label><input className={inputCls} value={v.callWindowEnd} onChange={e => setV(p => ({ ...p, callWindowEnd: e.target.value }))} placeholder="21:00" /></div>
            <div className="space-y-1.5"><label className="block text-xs font-normal text-[#64748B]">Timezone</label><input className={inputCls} value={v.callTimezone} onChange={e => setV(p => ({ ...p, callTimezone: e.target.value }))} placeholder="Asia/Kolkata" /></div>
            <div className="space-y-1.5"><label className="block text-xs font-normal text-[#64748B]">Daily cap (0=∞)</label><input type="number" className={inputCls} value={v.dailyCap} onChange={e => setV(p => ({ ...p, dailyCap: e.target.value }))} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 mt-3"><input type="checkbox" checked={v.requireConsent} onChange={e => setV(p => ({ ...p, requireConsent: e.target.checked }))} className="h-4 w-4 rounded" /> Require consent before calling</label>
        </div>

        <div className="flex justify-end">
          <button onClick={saveVoice} disabled={savingVoice} className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] px-4 h-10 text-sm font-medium text-white hover:bg-[#4F46E5] disabled:opacity-50">{savingVoice && <Loader2 className="h-4 w-4 animate-spin" />} Save Voice</button>
        </div>
      </div>

      {/* Do-not-call list */}
      <div className="rounded-xl border border-gray-100 bg-white p-5 space-y-3">
        <p className="text-sm font-semibold text-[#1E293B] flex items-center gap-2"><Ban className="h-4 w-4 text-red-500" /> Do-not-call list</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input className={inputCls} value={newDnc.phone} onChange={e => setNewDnc(p => ({ ...p, phone: e.target.value }))} placeholder="+919876543210" />
          <input className={inputCls} value={newDnc.reason} onChange={e => setNewDnc(p => ({ ...p, reason: e.target.value }))} placeholder="Reason (optional)" />
          <button onClick={addDnc} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-4 h-10 text-sm font-medium text-white hover:bg-slate-900 flex-shrink-0"><Plus className="h-4 w-4" /> Add</button>
        </div>
        {optOuts.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">No blocked numbers. Numbers that opt out during a call are added here automatically.</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {optOuts.map(o => (
              <div key={o.id} className="flex items-center justify-between py-2 text-sm">
                <div><span className="font-medium text-slate-700 tabular-nums">{o.phoneNorm}</span>{o.reason && <span className="text-slate-400"> · {o.reason}</span>}</div>
                <button onClick={() => removeDnc(o.id)} className="text-slate-300 hover:text-red-500" aria-label="Remove"><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Searchable prospect picker (id-aware, portalled via Floating) ────────────
function ProspectPicker({
  prospects, value, onChange, placeholder = 'Select a prospect…', includeNone = false, noneLabel = '— None —',
}: {
  prospects: ProspectLite[]; value: string; onChange: (id: string) => void;
  placeholder?: string; includeNone?: boolean; noneLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const label = (p: ProspectLite) => `${p.contactName} · ${p.companyName}`;
  const selected = prospects.find(p => p.id === value);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? prospects.filter(p => label(p).toLowerCase().includes(q) || (p.status || '').toLowerCase().includes(q))
    : prospects;

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 10);
    else setQuery('');
  }, [open]);

  const pick = (id: string) => { onChange(id); setOpen(false); setQuery(''); };

  return (
    <div ref={containerRef} className="relative w-full">
      <button type="button" onClick={() => setOpen(v => !v)}
        className={`w-full h-10 px-3 rounded-lg border text-sm text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors border-gray-200 bg-white hover:border-[#6366F1]/40 ${open ? 'ring-2 ring-[#6366F1] border-[#6366F1]/40' : ''}`}>
        <span className={`truncate flex-1 ${selected ? 'text-[#1E293B]' : 'text-gray-400'}`}>
          {selected ? label(selected) : placeholder}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <Floating open={open} anchorRef={containerRef} onClose={() => setOpen(false)}
        className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        style={{ maxHeight: 300, display: 'flex', flexDirection: 'column' }}>
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 flex-shrink-0">
          <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <input ref={inputRef} type="text" className="flex-1 text-sm bg-transparent outline-none text-[#1E293B] placeholder:text-gray-400"
            placeholder="Type to search…" value={query} onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && filtered.length === 1) pick(filtered[0].id); }} />
          {query && <button type="button" onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600"><X className="h-3 w-3" /></button>}
        </div>
        <div className="overflow-y-auto flex-1">
          {includeNone && (
            <button type="button" onClick={() => pick('')}
              className={`w-full text-left px-3 py-2 text-sm truncate ${!value ? 'bg-[#6366F1] text-white font-medium' : 'text-slate-500 hover:bg-indigo-50 hover:text-[#6366F1]'}`}>
              {noneLabel}
            </button>
          )}
          {filtered.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-400 text-center">No prospects found</p>
          ) : (
            filtered.map(p => (
              <button key={p.id} type="button" onClick={() => pick(p.id)}
                className={`w-full text-left px-3 py-2 text-sm truncate ${p.id === value ? 'bg-[#6366F1] text-white font-medium' : 'text-[#1E293B] hover:bg-indigo-50 hover:text-[#6366F1]'}`}>
                {label(p)} <span className={p.id === value ? 'text-indigo-100' : 'text-gray-400'}>({p.status})</span>
              </button>
            ))
          )}
        </div>
        {prospects.length > 10 && (
          <div className="px-3 py-1.5 border-t border-gray-50 bg-gray-50 flex-shrink-0">
            <p className="text-xs text-gray-400">{filtered.length} of {prospects.length} shown</p>
          </div>
        )}
      </Floating>
    </div>
  );
}

// ── Small helpers ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-[#6366F1]' : 'bg-gray-300'}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

function Drawer({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="block text-xs font-normal text-slate-500 mb-1">{label}</label>{children}</div>;
}
function Info({ label, value }: { label: string; value: string }) {
  return <div><div className="text-xs font-medium text-slate-400 capitalize">{label}</div><div className="text-slate-700 capitalize">{value}</div></div>;
}
function EmptyBox({ icon, title, hint }: { icon: React.ReactNode; title: string; hint: string }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 bg-slate-50/60 px-6 py-14 text-center">
      <div className="text-slate-300 mx-auto mb-3 flex justify-center">{icon}</div>
      <p className="text-sm font-semibold text-[#334155]">{title}</p>
      <p className="text-xs text-[#94A3B8] mt-1">{hint}</p>
    </div>
  );
}
